import { useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { API } from "@/lib/brand";

/**
 * Share-via-Email button for a calculator.
 * Opens a modal, collects Name + Email, POSTs to /api/calculator/share, toasts result.
 *
 * `buildPayload` returns the CalculatorPayload the backend expects:
 *   { type, title, one_liner, inputs: [{label,value}], hero: {label,value},
 *     summary: [{label,value,accent?}], row_headers?: string[], rows?: string[][] }
 */
export default function ShareByEmail({ buildPayload, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), email: email.trim(), calculator: buildPayload() };
      const res = await fetch(`${API}/calculator/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || "Could not send report");
      }
      toast.success("Report on its way", {
        description: "Check your inbox — the PDF is attached.",
      });
      setOpen(false);
      setName("");
      setEmail("");
    } catch (e) {
      toast.error(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-outline" data-testid={`${testIdPrefix}-share-open`}>
          <Mail size={16} /> Share via Email
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`${testIdPrefix}-share-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-ink">Email this report</DialogTitle>
          <DialogDescription className="text-[14px] text-ink-soft leading-relaxed">
            We'll send a branded PDF of this calculation to your inbox — great to review
            with family or refer back to before a consultation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <label className="block">
            <span className="eyebrow block mb-2">Your Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="fld"
              data-testid={`${testIdPrefix}-share-name`}
              autoFocus
            />
          </label>
          <label className="block">
            <span className="eyebrow block mb-2">Email Address</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="fld"
              data-testid={`${testIdPrefix}-share-email`}
            />
          </label>
        </div>

        <DialogFooter className="mt-2">
          <button
            onClick={submit}
            disabled={submitting}
            className="btn-brass disabled:opacity-60"
            data-testid={`${testIdPrefix}-share-submit`}
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Report</>}
          </button>
        </DialogFooter>

        <p className="text-[11px] text-ink-soft/85 leading-relaxed mt-1">
          We use your details only to send this report and, occasionally, related updates.
          Unsubscribe any time.
        </p>
      </DialogContent>
    </Dialog>
  );
}
